import { useState, useEffect } from "react";
import { fmtMoneyExact, Money } from "@/lib/money";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { F, th as palette, Over, Btn } from "@/lib/bricks";
import AddressAutocomplete, { mapProvince } from "@/components/AddressAutocomplete";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDay, fmtDateTime } from "@/lib/dates";
import { Page, PageHeader, Stats, StatusTabs, Controls, List, Row, Empty, Section, TextLink, Loading, humanize } from "@/components/calm/Calm";

const STATUS_LABEL = {
  active: 'Active',
  pending: 'Pending',
  completed: 'Completed',
  cancelled: 'Cancelled',
  fallen_through: 'Fell through',
};

const fmtCAD = fmtMoneyExact;


const EMPTY_FORM = {
  deal_type: 'Purchase',
  property_address: '',
  city: '',
  province: 'ON',
  mls_number: '',
  list_price: '',
  sale_price: '',
  acceptance_date: '',
  closing_date: '',
  contact_id: '',
  agent_id: '',
  commission_rate: '',
  commission_amount: '',
  notes: '',
};

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [contacts, setContacts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [activeTab, setActiveTab] = useState('Open');
  const [deadlines, setDeadlines] = useState([]);
  const { resolvedTheme } = useTheme();
  const { user, session } = useAuth();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);
  const navigate = useNavigate();
  const isManager = user?.role === 'manager' || user?.role === 'admin';
  const [importOn, setImportOn] = useState(false);

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  const load = () => {
    fetch(`${BASE}/api/transactions`, { headers })
      .then(r => r.json()).then(d => { setTransactions(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
    if (isManager) fetch(`${BASE}/api/deal-import/enabled`, { headers }).then(r => r.ok ? r.json() : null).then(d => setImportOn(!!d?.enabled)).catch(() => {});
    fetch(`${BASE}/api/deals/deadlines?days=14`, { headers }).then(r => r.ok ? r.json() : null).then(d => setDeadlines(Array.isArray(d?.items) ? d.items : [])).catch(() => {});
    fetch(`${BASE}/api/crm/contacts`, { headers }).then(r => r.json()).then(d => setContacts(Array.isArray(d) ? d : [])).catch(() => {});
    if (isManager) fetch(`${BASE}/api/agents`, { headers }).then(r => r.json()).then(d => setAgents(Array.isArray(d) ? d : [])).catch(() => {});
  }, []); // eslint-disable-line

  const handleCreate = async () => {
    if (!form.property_address || !form.deal_type) return toast.error("Address and deal type are required");
    try {
      const payload = { ...form, deal_type: (form.deal_type || "purchase").toLowerCase() };
      const res = await fetch(`${BASE}/api/transactions`, { method: 'POST', headers, body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.error || `HTTP ${res.status}`);
      }
      toast.success("Transaction created");
      setShowNew(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(e.message || "Failed to create transaction"); }
  };

  const CLOSED_TX = new Set(['completed', 'cancelled', 'fallen_through']);
  const isClosedTx = (tx) => CLOSED_TX.has(String(tx.status || '').toLowerCase());
  const TABS = [
    { id: 'Open', count: transactions.filter(tx => !isClosedTx(tx)).length },
    { id: 'Closed', count: transactions.filter(tx => isClosedTx(tx)).length },
    { id: 'All', count: transactions.length },
  ];
  const filtered = transactions.filter(tx => {
    if (activeTab === 'Closed') return isClosedTx(tx);
    if (activeTab === 'All') return true;
    return !isClosedTx(tx);
  });

  if (loading) return <Loading />;

  const totalVol = transactions.filter(t => t.status === 'completed').reduce((s, t) => s + (t.sale_price || 0), 0);
  const openCount = TABS[0].count;
  const urgent = deadlines.filter(d => d.state === 'overdue' || d.state === 'due_soon').length;

  return (
    <Page testid="transactions-page">
      <PageHeader
        title="Transactions"
        subtitle={importOn
          ? <>Every deal, deadline and closing in one place. <TextLink testid="tx-import-link" onClick={() => navigate('/transactions/import')}>Import deals</TextLink></>
          : "Every deal, deadline and closing in one place"}
        action={<Btn variant="primary" dark={dark} icon="plus" style={{ whiteSpace: 'nowrap' }} onClick={() => setShowNew(true)}>New transaction</Btn>}
      />

      <Stats items={[
        { label: 'Open', value: openCount },
        { label: 'Deadlines, next 14 days', value: deadlines.length, attention: urgent > 0 },
        { label: 'Closed volume', value: <Money value={totalVol} /> },
      ]} />

      {/* Deal deadlines: open conditions (overdue first), deposit due, closings in the next 14 days */}
      {deadlines.length > 0 && (
        <Section title="Next 14 days" testid="tx-deadlines">
          <List>
            {deadlines.slice(0, 6).map((d, i) => {
              const attention = d.state === 'overdue' || d.state === 'due_soon';
              const tag = d.state === 'overdue' ? 'Deadline passed' : (d.state === 'due_soon' ? 'Due soon' : (d.type === 'condition' ? 'Condition' : (d.type === 'deposit' ? 'Deposit' : 'Closing')));
              const when = d.type === 'condition' ? fmtDateTime(d.at) : fmtDay(d.date);
              return (
                <Row
                  key={`${d.type}-${d.condition_id || d.transaction_id}-${i}`}
                  onClick={() => navigate(`/transactions/${d.transaction_id}`)}
                  title={d.label}
                  meta={[d.property_address, { text: when, figure: true }]}
                  status={tag}
                  attention={attention}
                />
              );
            })}
          </List>
        </Section>
      )}

      <Controls>
        <StatusTabs testid="transactions-status-filter" value={activeTab} onChange={setActiveTab}
          options={TABS.map(t => ({ key: t.id, label: t.id }))} />
      </Controls>

      {filtered.length === 0 ? (
        <Empty action={<TextLink onClick={() => setShowNew(true)}>New transaction</TextLink>}>
          {activeTab === 'Closed' ? 'No closed deals yet.' : activeTab === 'Open' ? 'No open deals. Start one when an offer is accepted.' : 'No transactions yet. Add your first deal to track conditions, deposits and closing.'}
        </Empty>
      ) : (
        <List testid="transactions-list">
          {filtered.map(t => {
            const price = t.sale_price || t.list_price;
            const place = [t.city, t.province].filter(Boolean).join(', ');
            return (
              <Row
                key={t.id}
                testid="transaction-row"
                onClick={() => navigate(`/transactions/${t.id}`)}
                title={t.property_address}
                meta={[humanize(t.deal_type), t.agent_name, place].filter(Boolean)}
                next={[
                  price ? { text: fmtCAD(price), figure: true } : null,
                  t.closing_date ? { text: `Closes ${fmtDay(t.closing_date)}` } : { text: 'No closing date' },
                  t.mls_number ? { text: `MLS ${t.mls_number}`, figure: true } : null,
                ].filter(Boolean)}
                status={STATUS_LABEL[t.status] || humanize(t.status) || 'Draft'}
                attention={t.status === 'fallen_through'}
              />
            );
          })}
        </List>
      )}

      {/* New Transaction Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent style={{ maxWidth: 560, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, letterSpacing: -0.3, color: p.ink }}>New transaction</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Address and deal type are required.</DialogDescription>
          </DialogHeader>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <Over color={p.ink3}>Deal type</Over>
                <select value={form.deal_type} onChange={e => setForm({ ...form, deal_type: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                  {['Purchase', 'Sale', 'Lease', 'Referral'].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <Over color={p.ink3}>MLS number</Over>
                <input type="text" value={form.mls_number} onChange={e => setForm({ ...form, mls_number: e.target.value })} placeholder="e.g. X1234567"
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
              </div>
            </div>

            <div>
              <Over color={p.ink3}>Property address</Over>
              <AddressAutocomplete
                value={form.property_address}
                onChange={v => setForm(f => ({ ...f, property_address: v }))}
                onSelect={s => setForm(f => ({
                  ...f,
                  property_address: s.label || f.property_address,
                  city: s.city || f.city,
                  province: mapProvince(s.province) || f.province,
                }))}
                placeholder="e.g. 123 Maple St"
                p={p}
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <Over color={p.ink3}>City</Over>
                <input type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="Toronto"
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
              </div>
              <div>
                <Over color={p.ink3}>Province</Over>
                <select value={form.province} onChange={e => setForm({ ...form, province: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                  {['ON', 'BC', 'AB', 'MB', 'SK', 'QC', 'NS', 'NB', 'NL', 'PE'].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <Over color={p.ink3}>List price (CAD)</Over>
                <input type="number" value={form.list_price} onChange={e => setForm({ ...form, list_price: e.target.value })} placeholder="850000"
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.mono, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
              </div>
              <div>
                <Over color={p.ink3}>Sale price (CAD)</Over>
                <input type="number" value={form.sale_price} onChange={e => setForm({ ...form, sale_price: e.target.value })} placeholder="840000"
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.mono, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <Over color={p.ink3}>Acceptance date</Over>
                <input type="date" value={form.acceptance_date} onChange={e => setForm({ ...form, acceptance_date: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none', boxSizing: 'border-box' }}/>
              </div>
              <div>
                <Over color={p.ink3}>Closing date</Over>
                <input type="date" value={form.closing_date} onChange={e => setForm({ ...form, closing_date: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none', boxSizing: 'border-box' }}/>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <Over color={p.ink3}>Contact</Over>
                <select value={form.contact_id} onChange={e => setForm({ ...form, contact_id: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                  <option value="">Choose a contact</option>
                  {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                </select>
              </div>
              <div>
                <Over color={p.ink3}>Commission rate (%)</Over>
                <input type="number" step="0.1" value={form.commission_rate} onChange={e => setForm({ ...form, commission_rate: e.target.value })} placeholder="2.5"
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.mono, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
              </div>
            </div>

            <div>
              <Over color={p.ink3}>Or flat commission (CAD)</Over>
              <input type="number" step="1" value={form.commission_amount} onChange={e => setForm({ ...form, commission_amount: e.target.value })} placeholder="Optional, overrides the rate"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.mono, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
            </div>

            {isManager && (
              <div>
                <Over color={p.ink3}>Assign agent</Over>
                <select value={form.agent_id} onChange={e => setForm({ ...form, agent_id: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                  <option value="">Choose an agent</option>
                  {agents.map(a => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}
                </select>
              </div>
            )}

            <div>
              <Over color={p.ink3}>Notes</Over>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Anything worth remembering"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}/>
            </div>
          </div>

          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowNew(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleCreate}>Create transaction</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
